import { Button } from "@progress/kendo-react-buttons";
import {
  DateRangePicker,
  DateRangePickerChangeEvent,
  SelectionRange,
} from "@progress/kendo-react-dateinputs";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { Grid, GridColumn, GridCustomFooterCellProps } from "@progress/kendo-react-grid";
import { Loader } from "@progress/kendo-react-indicators";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Error } from "@progress/kendo-react-labels";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import analyticsApi from "../../api/analytics";
import { useUserContext } from "../../hooks/useUserContext";

export const TradeSummary = () => {
  const currentYear = new Date().getFullYear();
  const januaryFirst = new Date(currentYear, 0, 1);
  const today = new Date();

  const [dateRange, setDateRange] = useState<SelectionRange>({
    start: januaryFirst,
    end: today,
  });
  const [submittedRange, setSubmittedRange] = useState<SelectionRange | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDemoCompanies, setShowDemoCompanies] = useState(false);

  const { context: user } = useUserContext();
  const isRiskTaker = user.roles.isRiskTaker;
  const isRangeComplete = !!(dateRange.start && dateRange.end);

  const submittedStart = submittedRange?.start?.toISOString().split("T")[0];
  const submittedEnd = submittedRange?.end?.toISOString().split("T")[0];

  const { data, isFetching, isError, error } = useQuery({
    queryKey: ["tradeSummary", submittedStart, submittedEnd],
    queryFn: () => analyticsApi.getTradeSummary(submittedStart!, submittedEnd!),
    enabled: !!(submittedRange?.start && submittedRange?.end),
    placeholderData: keepPreviousData,
  });

  // Auto-fire on mount with default date range
  useEffect(() => {
    setSubmittedRange({ start: januaryFirst, end: today });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const _export = useRef<ExcelExport>(null);
  const _grid = useRef<any>(null);
  const pickerRef = useRef<any>(null);

  const handleDateRangeChange = (event: DateRangePickerChangeEvent) => {
    const {
      value: { start, end },
    } = event;
    if (pickerRef.current && start && end) {
      pickerRef.current.setShow(false);
    }
    setDateRange(event.value);
  };

  const handleRunReport = () => {
    if (isRangeComplete) {
      setSubmittedRange({ start: dateRange.start!, end: dateRange.end! });
      setIsEditing(false);
    }
  };

  const formatDateDisplay = (range: SelectionRange) => {
    const fmt = (d: Date) =>
      d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return `${fmt(range.start!)} – ${fmt(range.end!)}`;
  };

  const allTradeData = data?.data || [];

  const hasDemoCompanies = useMemo(() => {
    return allTradeData.some((item) => item.isDemo);
  }, [allTradeData]);

  const notionalTradeData = useMemo(() => {
    if (showDemoCompanies) {
      return allTradeData;
    }
    return allTradeData.filter((item) => !item.isDemo);
  }, [allTradeData, showDemoCompanies]);

  const excelExport = () => {
    if (_export.current && _grid.current) {
      _export.current.save(notionalTradeData, _grid.current.columns);
    }
  };

  const TotalCell = ({ field }: GridCustomFooterCellProps) => {
    const total = notionalTradeData.reduce((acc, item) => acc + (item[field] || 0), 0);

    let formattedTotal;
    if (field === "totalVolume") {
      formattedTotal = Math.round(total).toLocaleString();
    } else {
      formattedTotal = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Math.round(total));
    }

    return (
      <td className="align-right" style={{ border: "none" }}>
        {formattedTotal}
      </td>
    );
  };

  const FeePerGallonTotalCell = () => {
    const totalVolume = notionalTradeData.reduce((acc, item) => acc + (item.totalVolume || 0), 0);
    const totalRiskTakerFee = notionalTradeData.reduce(
      (acc, item) => acc + (item.riskTakerFee || 0),
      0,
    );
    const feePerGallon = totalVolume > 0 ? totalRiskTakerFee / totalVolume : 0;

    return (
      <td className="align-right" style={{ border: "none" }}>
        {feePerGallon.toFixed(4)}
      </td>
    );
  };

  const BrokerFeePerGallonTotalCell = () => {
    const totalVolume = notionalTradeData.reduce((acc, item) => acc + (item.totalVolume || 0), 0);
    const totalBrokerFee = notionalTradeData.reduce((acc, item) => acc + (item.brokerFee || 0), 0);
    const feePerGallon = totalVolume > 0 ? totalBrokerFee / totalVolume : 0;

    return (
      <td className="align-right" style={{ border: "none" }}>
        {feePerGallon.toFixed(4)}
      </td>
    );
  };

  return (
    <div style={{ padding: "1rem" }}>
      <div style={{ marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: "1rem" }}>
        <label style={{ fontWeight: "bold", color: "#333" }}>Date Range:</label>
        <span style={{ color: "#333", fontSize: "14px" }}>
          {submittedRange && formatDateDisplay(submittedRange)}
        </span>

        {isEditing ? (
          <Button
            themeColor="primary"
            onClick={handleRunReport}
            disabled={!isRangeComplete || isFetching}
          >
            Run Report
          </Button>
        ) : (
          <Button onClick={() => setIsEditing(true)} disabled={isFetching}>
            Edit Dates
          </Button>
        )}

        {!isEditing && hasDemoCompanies && (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Checkbox
              checked={showDemoCompanies}
              onChange={(e) => setShowDemoCompanies(e.value)}
            />
            <label style={{ color: "#333", fontSize: "14px" }}>Show Demo Companies</label>
          </div>
        )}

        {!isEditing && notionalTradeData.length > 0 && (
          <Button themeColor="primary" onClick={excelExport} style={{ marginLeft: "auto" }}>
            Export to Excel
          </Button>
        )}
      </div>

      {isEditing && (
        <div style={{ marginBottom: "1rem" }}>
          <DateRangePicker
            ref={pickerRef}
            value={dateRange}
            min={new Date(2025, 0, 1)}
            max={today}
            onChange={handleDateRangeChange}
            style={{ width: "300px" }}
          />
        </div>
      )}

      {isError && (
        <Error>
          Error loading trade summary{error instanceof Error ? ": " + error.message : ""}
        </Error>
      )}

      {isFetching && !isEditing ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
          <Loader size="large" type="infinite-spinner" />
        </div>
      ) : notionalTradeData.length > 0 ? (
        <div style={{ position: "relative" }}>
          {isEditing && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(255, 255, 255, 0.6)",
                zIndex: 1,
                borderRadius: "4px",
              }}
            />
          )}
          <Tooltip anchorElement="target" position="top" parentTitle={true}>
            <ExcelExport ref={_export} fileName={`TradeSummary_${submittedStart}_to_${submittedEnd}.xlsx`} />
            <Grid
              ref={_grid}
              data={notionalTradeData}
              sortable={true}
              autoProcessData={true}
              style={{ maxHeight: "500px" }}
            >
              <GridColumn field="client" title="Client" />
              <GridColumn field="broker" title="Broker" />
              <GridColumn
                field="totalVolume"
                title="Total Volume"
                format="{0:n0}"
                className="align-right"
                headerClassName="justify-end"
                cells={{ footerCell: TotalCell }}
              />
              {isRiskTaker && (
                <GridColumn
                  field="fairValue"
                  title="Fair Value"
                  format="{0:c0}"
                  className="align-right"
                  headerClassName="justify-end"
                  cells={{ footerCell: TotalCell }}
                />
              )}
              <GridColumn
                field="riskTakerPrice"
                title="Risk Taker Price"
                format="{0:c0}"
                className="align-right"
                headerClassName="justify-end"
                cells={{ footerCell: TotalCell }}
              />
              <GridColumn
                field="brokerPrice"
                title="Broker Price"
                format="{0:c0}"
                className="align-right"
                headerClassName="justify-end"
                cells={{ footerCell: TotalCell }}
              />
              <GridColumn
                field="brokerFee"
                title="Broker Fee"
                format="{0:c0}"
                className="align-right"
                headerClassName="justify-end"
                cells={{ footerCell: TotalCell }}
              />
              {isRiskTaker && (
                <GridColumn
                  field="riskTakerFee"
                  title="Risk Taker Fee"
                  format="{0:c0}"
                  className="align-right"
                  headerClassName="justify-end"
                  cells={{ footerCell: TotalCell }}
                />
              )}
              {isRiskTaker && (
                <GridColumn
                  field="feePerGallon"
                  title="Fee Per Gallon"
                  format="{0:n4}"
                  className="align-right"
                  headerClassName="justify-end"
                  cells={{ footerCell: FeePerGallonTotalCell }}
                />
              )}
              <GridColumn
                field="brokerFeePerGallon"
                title="Broker Fee Per Gallon"
                format="{0:n4}"
                className="align-right"
                headerClassName="justify-end"
                cells={{ footerCell: BrokerFeePerGallonTotalCell }}
              />
            </Grid>
          </Tooltip>
        </div>
      ) : !isEditing && submittedRange ? (
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            color: "#666",
            backgroundColor: "#f9f9f9",
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
          }}
        >
          <p>No trade summary data found for the specified date range.</p>
        </div>
      ) : null}
    </div>
  );
};
